import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import type { BreakdownAnalysis } from '../types/dashboard.types';

interface BreakdownAnalysisSectionProps {
  analysis: BreakdownAnalysis;
}

const BAR_COLOR = '#f97316';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-shadow-lg">
      <p className="tw-font-semibold tw-text-gray-900 tw-mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="tw-text-sm tw-text-gray-600">
          {entry.value} véhicule{entry.value > 1 ? 's' : ''} ({entry.payload.percentage}%)
        </p>
      ))}
    </div>
  );
};

export const BreakdownAnalysisSection: React.FC<
  BreakdownAnalysisSectionProps
> = ({ analysis }) => {
  const isMobile = useIsMobile();
  const tickFontSize = isMobile ? 9 : 11;
  const brandYAxisWidth = isMobile ? 55 : 70;
  const modelYAxisWidth = isMobile ? 70 : 90;
  const barSize = isMobile ? 18 : 24;

  const brandData = analysis.byBrand.map((b) => ({
    name: b.brand,
    count: b.count,
    percentage: b.percentage,
  }));

  const modelData = analysis.byModel.map((m) => ({
    name: `${m.brand} ${m.model}`,
    count: m.count,
    percentage: m.percentage,
  }));

  return (
    <div>
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4 tw-mb-4">
        {/* Pannes par marque */}
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 sm:tw-p-6 tw-overflow-hidden">
          <h3 className="tw-text-sm sm:tw-text-base md:tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-1">
            Véhicules en panne par marque
          </h3>
          <p className="tw-text-xs tw-text-gray-400 tw-mb-4">Un véhicule avec plusieurs pannes n'est comptabilisé qu'une fois</p>
          {brandData.length === 0 ? (
            <p className="tw-text-sm tw-text-gray-500 tw-text-center tw-py-8">
              Aucune donnée disponible
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, brandData.length * (isMobile ? 32 : 40))}>
              <BarChart data={brandData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: tickFontSize }} allowDecimals={false} domain={[0, 'dataMax']} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: '#374151', fontSize: tickFontSize }}
                  width={brandYAxisWidth}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={barSize} fill={BAR_COLOR} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pannes par modèle */}
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 sm:tw-p-6 tw-overflow-hidden">
          <h3 className="tw-text-sm sm:tw-text-base md:tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-1">
            Véhicules en panne par modèle
          </h3>
          <p className="tw-text-xs tw-text-gray-400 tw-mb-4">Un véhicule avec plusieurs pannes n'est comptabilisé qu'une fois</p>
          {modelData.length === 0 ? (
            <p className="tw-text-sm tw-text-gray-500 tw-text-center tw-py-8">
              Aucune donnée disponible
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, modelData.length * (isMobile ? 32 : 40))}>
              <BarChart data={modelData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: tickFontSize }} allowDecimals={false} domain={[0, 'dataMax']} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: '#374151', fontSize: tickFontSize }}
                  width={modelYAxisWidth}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={barSize} fill={BAR_COLOR} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

    </div>
  );
};
